import type { DtoolsDisplaySettings, DtoolsPortalPaymentTerm } from "./dtoolsDisplaySettings";
import { commercialDraftSignature } from "./dtoolsCommercialDraftSync";
import type { DtoolsStagingSession } from "./dtoolsJsonStaging";
import { buildReviewHierarchy } from "./dtoolsJsonStaging";
import type { DtoolsSystemContent } from "./dtoolsProposalContent";
import type { DtoolsProposalPresentation } from "./dtoolsProposalPresentation";
import { withInternalSystemsHiddenByDefault } from "./dtoolsOptionVisibility";
import {
  DTOOLS_TAB_MODEL_VERSION_CLASSIFICATION,
  normalizeSystemClassificationsForSave,
  type DtoolsSystemClassificationsMap,
  type DtoolsTabModelVersion,
} from "./dtoolsSystemClassification";
import {
  FULFILMENT_TIMELINE_TYPES,
  isStrictYearMonth,
  type FulfilmentTimelineType,
  type ProcurementTrackingOrigin,
} from "./proposalFulfilmentTimeline";
import type { ProposalDiscountMethod } from "./proposalDiscountMethods";

/** Staff draft for three-method pricing adjustment on the D-Tools assembly. */
export type DtoolsReviewAssemblyNegotiatedPricing = {
  discountMethod?: ProposalDiscountMethod | "none" | null;
  discountPercentage?: string | null;
  specialDiscountSgd?: string | null;
  agreedPriceSgd?: string | null;
  agreedPriceBasis?: "before_gst" | "gst_inclusive" | null;
  showDiscountPctToCustomer?: boolean | null;
  invoiceGstEnabled?: boolean;
  markupEnabled?: boolean;
  markupPercentage?: string | null;
};

/** Persisted Target goods-ready / delivery timing on the D-Tools staging assembly. */
export type DtoolsReviewAssemblyFulfilmentTimeline = {
  fulfilmentTimelineType: FulfilmentTimelineType;
  targetFulfilmentMonth: string | null;
  procurementTrackingOrigin: Extract<ProcurementTrackingOrigin, "portal_native" | "legacy_import">;
  /** True only after staff explicitly chooses ASAP or Month/year in Setup/Review. */
  explicitlySelected: boolean;
};

export const DTOOLS_FULFILMENT_TIMELINE_DOM_ID = "dtools-fulfilment-timeline";

export function unsetDtoolsReviewFulfilmentTimeline(): DtoolsReviewAssemblyFulfilmentTimeline {
  return {
    fulfilmentTimelineType: "unset",
    targetFulfilmentMonth: null,
    procurementTrackingOrigin: "portal_native",
    explicitlySelected: false,
  };
}

function asTimelineType(value: unknown): FulfilmentTimelineType {
  const raw = String(value ?? "unset").trim();
  return (FULFILMENT_TIMELINE_TYPES as readonly string[]).includes(raw)
    ? (raw as FulfilmentTimelineType)
    : "unset";
}

function asTrackingOrigin(
  value: unknown,
): Extract<ProcurementTrackingOrigin, "portal_native" | "legacy_import"> {
  return value === "legacy_import" ? "legacy_import" : "portal_native";
}

/** Normalise raw assembly / proposal timing into the staging authority shape. */
export function normaliseDtoolsReviewFulfilmentTimeline(
  raw: Partial<DtoolsReviewAssemblyFulfilmentTimeline> | null | undefined,
): DtoolsReviewAssemblyFulfilmentTimeline {
  if (!raw || typeof raw !== "object") {
    return unsetDtoolsReviewFulfilmentTimeline();
  }
  const type = asTimelineType(raw.fulfilmentTimelineType);
  let month =
    raw.targetFulfilmentMonth != null && String(raw.targetFulfilmentMonth).trim()
      ? String(raw.targetFulfilmentMonth).trim()
      : null;
  if (type === "asap" || type === "unset") month = null;
  if (type === "month_year" && month && !isStrictYearMonth(month)) month = null;
  return {
    fulfilmentTimelineType: type,
    targetFulfilmentMonth: month,
    procurementTrackingOrigin: asTrackingOrigin(raw.procurementTrackingOrigin),
    explicitlySelected: raw.explicitlySelected === true && (type === "asap" || type === "month_year"),
  };
}

/** Hydrate from a saved proposal header without marking an explicit staff override. */
export function hydrateDtoolsReviewFulfilmentTimelineFromProposal(input: {
  fulfilmentTimelineType?: string | null;
  targetFulfilmentMonth?: string | null;
  procurementTrackingOrigin?: string | null;
}): DtoolsReviewAssemblyFulfilmentTimeline {
  const type = asTimelineType(input.fulfilmentTimelineType);
  let month =
    input.targetFulfilmentMonth != null && String(input.targetFulfilmentMonth).trim()
      ? String(input.targetFulfilmentMonth).trim()
      : null;
  if (type === "asap" || type === "unset") month = null;
  if (type === "month_year" && month && !isStrictYearMonth(month)) month = null;
  return {
    fulfilmentTimelineType: type,
    targetFulfilmentMonth: month,
    procurementTrackingOrigin: asTrackingOrigin(input.procurementTrackingOrigin),
    explicitlySelected: false,
  };
}

export function fulfilmentTimelineFieldsValueFromAssembly(
  timeline: DtoolsReviewAssemblyFulfilmentTimeline | null | undefined,
): {
  fulfilmentTimelineType: "asap" | "month_year" | "unset";
  targetFulfilmentMonth: string;
} {
  const normalised = normaliseDtoolsReviewFulfilmentTimeline(timeline);
  return {
    fulfilmentTimelineType: normalised.fulfilmentTimelineType,
    targetFulfilmentMonth: normalised.targetFulfilmentMonth ?? "",
  };
}

export function assemblyFulfilmentTimelineFromFieldsValue(
  value: {
    fulfilmentTimelineType: "asap" | "month_year" | "unset";
    targetFulfilmentMonth: string;
  },
  previous?: DtoolsReviewAssemblyFulfilmentTimeline | null,
): DtoolsReviewAssemblyFulfilmentTimeline {
  const type = value.fulfilmentTimelineType;
  const month =
    type === "month_year" && isStrictYearMonth(value.targetFulfilmentMonth)
      ? value.targetFulfilmentMonth.trim()
      : type === "month_year"
        ? String(value.targetFulfilmentMonth ?? "").trim() || null
        : null;
  const explicitlySelected = type === "asap" || type === "month_year";
  return {
    fulfilmentTimelineType: type,
    targetFulfilmentMonth: type === "asap" || type === "unset" ? null : month,
    procurementTrackingOrigin: previous?.procurementTrackingOrigin ?? "portal_native",
    explicitlySelected,
  };
}

export type DtoolsFulfilmentTimelineClientValidation =
  | { ok: true; timeline: DtoolsReviewAssemblyFulfilmentTimeline }
  | { ok: false; message: string };

/** Client-side save gate — mirrors portal-native create requirements without trusting the browser as authority. */
export function validateDtoolsFulfilmentTimelineForConfirm(
  timeline: DtoolsReviewAssemblyFulfilmentTimeline | null | undefined,
  options?: { allowInheritOnRevision?: boolean },
): DtoolsFulfilmentTimelineClientValidation {
  const normalised = normaliseDtoolsReviewFulfilmentTimeline(timeline);
  if (options?.allowInheritOnRevision && !normalised.explicitlySelected) {
    return { ok: true, timeline: normalised };
  }
  if (normalised.fulfilmentTimelineType === "unset") {
    return { ok: false, message: "Select ASAP or a target month." };
  }
  if (normalised.fulfilmentTimelineType === "asap") {
    return {
      ok: true,
      timeline: {
        ...normalised,
        targetFulfilmentMonth: null,
        explicitlySelected: true,
      },
    };
  }
  if (!isStrictYearMonth(normalised.targetFulfilmentMonth)) {
    return { ok: false, message: "Select ASAP or a target month." };
  }
  return {
    ok: true,
    timeline: {
      ...normalised,
      targetFulfilmentMonth: normalised.targetFulfilmentMonth,
      explicitlySelected: true,
    },
  };
}

/** Pre-create proposal assembly state persisted on the staging session. */
export type DtoolsReviewAssembly = {
  paymentTerms?: DtoolsPortalPaymentTerm[];
  displaySettings?: DtoolsDisplaySettings;
  systemContent?: Record<string, DtoolsSystemContent>;
  presentation?: DtoolsProposalPresentation;
  systemClassifications?: DtoolsSystemClassificationsMap;
  dtoolsTabModelVersion?: DtoolsTabModelVersion;
  /** Set when staff manually edits the proposal title — Cleo must not overwrite. */
  proposalTitleUserEdited?: boolean;
  /** Target goods-ready / delivery timing (Setup + Review share this authority). */
  fulfilmentTimeline?: DtoolsReviewAssemblyFulfilmentTimeline;
  /** Three-method pricing adjustment draft (percentage / fixed / final agreed). */
  negotiatedPricing?: DtoolsReviewAssemblyNegotiatedPricing;
};

export function defaultReviewAssemblyFromSession(session: DtoolsStagingSession): DtoolsReviewAssembly {
  const proposalName = String(session.parseResult.project.proposalName ?? "").trim();
  const customerName = String(session.parseResult.customer?.clientName ?? "").trim();
  const systems = buildReviewHierarchy(session.lines).systems;
  return {
    paymentTerms: [],
    displaySettings: withInternalSystemsHiddenByDefault(systems, { showPaymentTerms: false }),
    systemContent: {},
    presentation: {
      proposalTitle: proposalName || null,
      proposalSubtitle: customerName ? `Prepared for ${customerName}` : null,
      customerIntroduction: null,
      heroImageUrl: null,
      systemDisplayNames: {},
      proposalDisclaimers: [],
    },
    systemClassifications: {},
    dtoolsTabModelVersion: DTOOLS_TAB_MODEL_VERSION_CLASSIFICATION,
    fulfilmentTimeline: unsetDtoolsReviewFulfilmentTimeline(),
  };
}

export function readDtoolsReviewAssembly(session: DtoolsStagingSession): DtoolsReviewAssembly {
  const raw = session.reviewAssembly;
  if (!raw || typeof raw !== "object") {
    return defaultReviewAssemblyFromSession(session);
  }
  const defaults = defaultReviewAssemblyFromSession(session);
  return {
    paymentTerms: raw.paymentTerms?.length ? raw.paymentTerms : [],
    displaySettings: { ...defaults.displaySettings, ...(raw.displaySettings ?? {}) },
    systemContent: { ...(defaults.systemContent ?? {}), ...(raw.systemContent ?? {}) },
    presentation: {
      ...(defaults.presentation ?? {}),
      ...(raw.presentation ?? {}),
      systemDisplayNames: {
        ...(defaults.presentation?.systemDisplayNames ?? {}),
        ...(raw.presentation?.systemDisplayNames ?? {}),
      },
      proposalDisclaimers: raw.presentation?.proposalDisclaimers ?? defaults.presentation?.proposalDisclaimers ?? [],
    },
    systemClassifications: {
      ...(defaults.systemClassifications ?? {}),
      ...(raw.systemClassifications ?? {}),
    },
    dtoolsTabModelVersion: raw.dtoolsTabModelVersion ?? defaults.dtoolsTabModelVersion,
    proposalTitleUserEdited: raw.proposalTitleUserEdited === true,
    fulfilmentTimeline: normaliseDtoolsReviewFulfilmentTimeline(
      raw.fulfilmentTimeline ?? defaults.fulfilmentTimeline,
    ),
    negotiatedPricing: {
      ...(defaults.negotiatedPricing ?? {}),
      ...(raw.negotiatedPricing ?? {}),
    },
  };
}

export function mergeDtoolsReviewAssembly(
  session: DtoolsStagingSession,
  patch: Partial<DtoolsReviewAssembly>,
): DtoolsReviewAssembly {
  const existing = readDtoolsReviewAssembly(session);
  return {
    paymentTerms: patch.paymentTerms ?? existing.paymentTerms,
    displaySettings: patch.displaySettings
      ? { ...existing.displaySettings, ...patch.displaySettings }
      : existing.displaySettings,
    systemContent: patch.systemContent
      ? { ...existing.systemContent, ...patch.systemContent }
      : existing.systemContent,
    systemClassifications: patch.systemClassifications
      ? { ...(existing.systemClassifications ?? {}), ...patch.systemClassifications }
      : existing.systemClassifications,
    dtoolsTabModelVersion: patch.dtoolsTabModelVersion ?? existing.dtoolsTabModelVersion,
    presentation: patch.presentation
      ? {
          ...existing.presentation,
          ...patch.presentation,
          systemDisplayNames: patch.presentation.systemDisplayNames
            ? { ...(existing.presentation?.systemDisplayNames ?? {}), ...patch.presentation.systemDisplayNames }
            : existing.presentation?.systemDisplayNames,
          proposalDisclaimers:
            patch.presentation.proposalDisclaimers ?? existing.presentation?.proposalDisclaimers,
        }
      : existing.presentation,
    proposalTitleUserEdited:
      patch.proposalTitleUserEdited ?? existing.proposalTitleUserEdited,
    fulfilmentTimeline: patch.fulfilmentTimeline
      ? normaliseDtoolsReviewFulfilmentTimeline(patch.fulfilmentTimeline)
      : existing.fulfilmentTimeline,
    negotiatedPricing: patch.negotiatedPricing
      ? { ...(existing.negotiatedPricing ?? {}), ...patch.negotiatedPricing }
      : existing.negotiatedPricing,
  };
}

export function reviewAssemblySignature(assembly: DtoolsReviewAssembly): string {
  const timeline = normaliseDtoolsReviewFulfilmentTimeline(assembly.fulfilmentTimeline);
  return JSON.stringify({
    commercial: commercialDraftSignature(assembly.paymentTerms ?? [], assembly.displaySettings ?? {}),
    systemContent: assembly.systemContent ?? {},
    presentation: assembly.presentation ?? {},
    systemClassifications: assembly.systemClassifications ?? {},
    dtoolsTabModelVersion: assembly.dtoolsTabModelVersion,
    proposalTitleUserEdited: assembly.proposalTitleUserEdited === true,
    fulfilmentTimeline: {
      fulfilmentTimelineType: timeline.fulfilmentTimelineType,
      targetFulfilmentMonth: timeline.targetFulfilmentMonth,
      procurementTrackingOrigin: timeline.procurementTrackingOrigin,
      explicitlySelected: timeline.explicitlySelected,
    },
    negotiatedPricing: assembly.negotiatedPricing ?? {},
  });
}

export function mergeDtoolsReviewAssemblyIfChanged(
  session: DtoolsStagingSession,
  prev: DtoolsReviewAssembly | null,
  patch: Partial<DtoolsReviewAssembly>,
): { assembly: DtoolsReviewAssembly; changed: boolean } {
  const assembly = mergeDtoolsReviewAssembly(
    { ...session, reviewAssembly: prev ?? session.reviewAssembly },
    patch,
  );
  if (!prev) return { assembly, changed: true };
  return {
    assembly,
    changed: reviewAssemblySignature(prev) !== reviewAssemblySignature(assembly),
  };
}

export function applyReviewAssemblyToStagingSession(
  session: DtoolsStagingSession,
  patch: Partial<DtoolsReviewAssembly>,
): DtoolsStagingSession {
  return {
    ...session,
    updatedAt: new Date().toISOString(),
    reviewAssembly: mergeDtoolsReviewAssembly(session, patch),
  };
}

export type DtoolsReviewAssemblyConfirmMetadata = {
  dtoolsPaymentTerms?: DtoolsPortalPaymentTerm[];
  dtoolsDisplaySettings?: DtoolsDisplaySettings;
  dtoolsSystemContent?: Record<string, DtoolsSystemContent>;
  dtoolsProposalPresentation?: DtoolsProposalPresentation;
  dtoolsSystemClassifications?: DtoolsSystemClassificationsMap;
  dtoolsTabModelVersion?: DtoolsTabModelVersion;
  dtoolsNegotiatedPricing?: DtoolsReviewAssemblyNegotiatedPricing;
};

export function reviewAssemblyToConfirmMetadata(
  assembly: DtoolsReviewAssembly,
): DtoolsReviewAssemblyConfirmMetadata {
  return {
    dtoolsPaymentTerms: assembly.paymentTerms,
    dtoolsDisplaySettings: assembly.displaySettings,
    dtoolsSystemContent: assembly.systemContent,
    dtoolsProposalPresentation: assembly.presentation,
    dtoolsSystemClassifications: normalizeSystemClassificationsForSave(assembly.systemClassifications),
    dtoolsTabModelVersion: assembly.dtoolsTabModelVersion,
    dtoolsNegotiatedPricing: assembly.negotiatedPricing,
  };
}
